/*
Copyright 2022-2024 New Vector Ltd.
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

export interface PrenigmaCollaborationRuntimeConfigOptions {
  enabled?: boolean;
  service?: string;
  source?: {
    system?: string;
    namespace?: string;
    accepted_versions?: string[];
    producer?: string;
    source_instance_id_template?: string;
  };
  events?: {
    emitted?: string[];
    session_state?: boolean;
    command_receipts?: boolean;
    partial_transcript?: boolean;
    final_transcript?: boolean;
    barge_in?: boolean;
    tts_cancel?: boolean;
    no_transcript?: boolean;
    streaming_voice_turn_evidence?: boolean;
    raw_audio_persistence?: boolean;
  };
  evidence?: {
    streaming_voice_turn?: {
      backend_ingest_api_path?: string;
      backend_ingest_http_method?: string;
      backend_readback_required?: boolean;
      backend_store?: string;
      host_slot?: string;
      producer?: string;
      raw_audio_persistence?: boolean;
      raw_secret_material_returned?: boolean;
      raw_transcript_persistence?: boolean;
      schema?: string;
    };
  };
  delivery?: {
    event_sink_binding?: string;
    event_sink_api_path?: string;
    event_sink_http_method?: string;
    timeline_readback_api_path?: string;
    timeline_readback_http_method?: string;
    authenticated_product_studio_session_required?: boolean;
    durable_outbox_required?: boolean;
    durable_outbox_storage?: string;
    matrix_publication_owner?: string;
  };
  backend_api?: {
    ingest_path?: string;
    ingest_method?: string;
    // Where to obtain a central-custody signature for an envelope. The block
    // named the key binding but never the route, so the only client that signs
    // — the live-evidence harness — hardcodes the path. A production producer
    // has to read it from somewhere.
    signature_path?: string;
    signature_method?: string;
    timeline_readback_path?: string;
    timeline_readback_method?: string;
    route_template?: string;
    authentication?: string;
    permission_for_ingest?: string;
    permission_for_readback?: string;
    raw_secret_material_required?: boolean;
    raw_secret_material_returned?: boolean;
  };
  signing?: {
    required?: boolean;
    signature_profile?: string;
    canonicalization?: string;
    digest?: string;
    key_binding?: string;
    raw_secret_material_required?: boolean;
    raw_secret_material_returned?: boolean;
  };
  matrix_readback?: {
    required?: boolean;
    room_context_required?: boolean;
    source_event_id_required?: boolean;
    timeline_readback_required?: boolean;
  };
  runtime_policy?: {
    product_local_voice_stack?: boolean;
    product_local_verifier?: boolean;
    product_local_bridge?: boolean;
    requires_bootstrap_injection?: boolean;
    live_evidence_required_before_release?: boolean;
    streaming_voice_evidence_required_before_release?: boolean;
  };
}

export interface ConfigOptions {
  /**
   * The Posthog endpoint to which analytics data will be sent.
   * This is only used in the full package of Element Call.
   */
  posthog?: {
    api_key: string;
    api_host: string;
  };

  /**
   * The Sentry endpoint to which crash data will be sent.
   * This is only used in the full package of Element Call.
   */
  sentry?: {
    DSN: string;
    environment: string;
  };

  /**
   * The rageshake server to which feedback and debug logs will be sent.
   * This is only used in the full package of Element Call.
   */
  rageshake?: {
    submit_url: string;
  };

  /**
   * Sets the URL to send opentelemetry data to. If unset, opentelemetry will
   * be disabled. This is only used in the full package of Element Call.
   */
  opentelemetry?: {
    collector_url: string;
  };

  // Describes the default homeserver to use. The same format as Element Web
  // (without identity servers as we don't use them).
  default_server_config?: {
    ["m.homeserver"]: {
      base_url: string;
      server_name: string;
    };
  };

  // Static OIDC client ids keyed by issuer URL, matching Element Web's
  // config.json shape. If absent, Element Call will try dynamic client
  // registration with the Matrix Authentication Service.
  oidc_static_clients?: Record<
    string,
    {
      client_id: string;
    }
  >;

  // Optional metadata used for dynamic OIDC client registration.
  oidc_metadata?: {
    client_name?: string;
    client_uri?: string;
    logo_uri?: string;
    contacts?: string[];
    tos_uri?: string;
    policy_uri?: string;
  };

  // Optional Prenigma/Peitho customer-demo branding. This is presentation-only
  // and must not contain Matrix, MAS, LiveKit, or credential material.
  prenigma_branding?: {
    product_id?: string;
    brand_name?: string;
    customer_name?: string;
    experience_name?: string;
    surface_label?: string;
    accent_color?: string;
    product_studio_control_plane?: string;
  };

  // Optional Prenigma collaboration source-event runtime contract. It is
  // configuration metadata only; live signing keys are supplied through
  // runtime injection, not config.json.
  prenigma_collaboration_runtime?: PrenigmaCollaborationRuntimeConfigOptions;

  // Matches the Element Web / deployment config knob used by the current
  // Chain Care Element Call config to expose SSO on the welcome/login surface.
  sso_redirect_options?: {
    immediate?: boolean;
    on_login_page?: boolean;
    on_welcome_page?: boolean;
  };

  // Optional identity-provider URL to visit after local Matrix/OIDC logout.
  logout_redirect_url?: string;

  // Describes the LiveKit configuration to be used.
  livekit?: {
    // The link to the service that returns a livekit url and token to use it.
    // This is a fallback link in case the homeserver in use does not advertise
    // a livekit service url in the client well-known.
    // The well known needs to be formatted like so:
    // {"type":"livekit", "livekit_service_url":"https://livekit.example.com"}
    // and stored under the key: "org.matrix.msc4143.rtc_foci"
    livekit_service_url: string;
  };

  /**
   * TEMPORARY experimental features.
   */
  features?: {
    /**
     * Allow to join group calls without audio and video.
     */
    feature_group_calls_without_video_and_audio?: boolean;

    /**
     * Send device-specific call session membership state events instead of
     * legacy user-specific call membership state events.
     * This setting has no effect when the user joins an active call with
     * legacy state events. For compatibility, Element Call will always join
     * active legacy calls with legacy state events.
     */
    feature_use_device_session_member_events?: boolean;
  };

  /**
   * A link to the software and services license agreement (SSLA)
   */
  ssla?: string;

  media_devices?: {
    /**
     * Defines whether participants should start with audio enabled by default.
     */
    enable_audio?: boolean;

    /**
     * Defines whether participants should start with video enabled by default.
     */
    enable_video?: boolean;
  };

  /**
   * Grace period in milliseconds to wait before reporting the sync loop as disconnected.
   * This allows brief sync interruptions without triggering a reconnection message.
   * Default is 10000ms (10 seconds). Set to 0 to disable the grace period.
   */
  sync_disconnect_grace_period_ms?: number;

  /**
   * These are low level options that are used to configure the MatrixRTC session.
   * Take care when changing these options.
   */
  matrix_rtc_session?: {
    /**
     * How long (in milliseconds) to wait before rotating end-to-end media encryption keys
     * when someone leaves a call.
     */
    wait_for_key_rotation_ms?: number;

    /**
     * The duration (in milliseconds) after the most recent keep-alive (delayed leave event restart)
     * that the server waits before sending the leave MatrixRTC membership event.
     */
    delayed_leave_event_delay_ms?: number;

    /**
     * The time (in milliseconds) after which we consider a delayed event restart http request to have failed.
     * Setting this to a lower value will result in more frequent retries but also a higher chance of failiour.
     *
     * In the presence of network packet loss (hurting TCP connections), the custom delayedEventRestartLocalTimeoutMs
     * helps by keeping more delayed event reset candidates in flight,
     * improving the chances of a successful reset. (its is equivalent to the js-sdk `localTimeout` configuration,
     * but only applies to calls to the `_unstable_updateDelayedEvent` endpoint with a body of `{action:"restart"}`.)
     */
    delayed_leave_event_restart_local_timeout_ms?: number;

    /**
     * The time interval (in milliseconds) at which the client sends membership keep-alive
     * messages to the server by restarting the timer for the delayed leave event.
     */
    delayed_leave_event_restart_ms?: number;

    /**
     * How long we wait before retrying after a network error on any of the requests.
     */
    network_error_retry_ms?: number;

    /**
     * The timeout (in milliseconds) after we joined the call, that our membership should expire
     * unless we have explicitly updated it.
     *
     * This is what goes into the m.rtc.member event expiry field and is typically set to a number of hours.
     */
    membership_event_expiry_ms?: number;
  };
}

// Overrides members from ConfigOptions that are always provided by the
// default config and are therefore non-optional.
export interface ResolvedConfigOptions extends ConfigOptions {
  default_server_config: {
    ["m.homeserver"]: {
      base_url: string;
      server_name: string;
    };
  };
  sync_disconnect_grace_period_ms: number;
  ssla: string;
  matrix_rtc_session: {
    wait_for_key_rotation_ms?: number;
    delayed_leave_event_delay_ms: number;
    delayed_leave_event_restart_local_timeout_ms?: number;
    delayed_leave_event_restart_ms?: number;
    network_error_retry_ms: number;
    membership_event_expiry_ms?: number;
  };
}

export const DEFAULT_CONFIG: ResolvedConfigOptions = {
  default_server_config: {
    ["m.homeserver"]: {
      base_url: "http://localhost:8008",
      server_name: "localhost",
    },
  },
  features: {
    feature_use_device_session_member_events: true,
  },
  sync_disconnect_grace_period_ms: 10000,
  ssla: "https://static.element.io/legal/element-software-and-services-license-agreement-uk-1.pdf",
  matrix_rtc_session: {
    delayed_leave_event_delay_ms: 10000,
    network_error_retry_ms: 1000,
  },
};
